import {
  Address,
  AloftArea,
  AloftPrice,
  MLSListing,
  ToolkitListingID,
  getAbodeMonthFromDateString,
  getLotAreaInSqft,
  isDefined,
  isUndefined,
  orUndefined,
  syntaxFNMA,
} from '@aloft/toolkit-shared';

import { proximityMiles } from '../../../utils/proximityMiles.ts';
import { CompSelectionProperty } from '../types.ts';
import { addQualityTiers } from './addQualityTiers.ts';
import { addSimilarityScores } from './addSimilarityScores.ts';
import { removeStreetSuffixes } from './removeStreetSuffixes.ts';

const areAddressesEqual = (address1: string, address2: string): boolean => {
  const addr1 = removeStreetSuffixes(address1);
  const addr2 = removeStreetSuffixes(address2);
  return addr1 === addr2;
};

const buildCompSelectionProperty = (
  listing: MLSListing,
  subject?: CompSelectionProperty,
  comps?: CompSelectionProperty[],
): CompSelectionProperty | undefined => {
  const subjectCoordinates =
    subject && subject.latitude && subject.longitude
      ? { lat: subject.latitude, lng: subject.longitude }
      : undefined;
  const saleMonth = getAbodeMonthFromDateString(listing.CloseDate);
  if (isUndefined(listing.UnparsedAddress)) return undefined;
  if (areAddressesEqual(listing.UnparsedAddress, subject?.address?.AddressLineText ?? '')) {
    return undefined; // Avoid including the subject property itself
  }

  const contractMonth = getAbodeMonthFromDateString(listing.PurchaseContractDate);
  const id = listing.ToolkitListingID;
  if (isUndefined(id)) return undefined;

  const saleDate = orUndefined(syntaxFNMA.isoMonthToAloftDate)(saleMonth);
  const contractDate = orUndefined(syntaxFNMA.isoMonthToAloftDate)(contractMonth);
  const listingContractDate = orUndefined(syntaxFNMA.isoMonthToAloftDate)(
    getAbodeMonthFromDateString(listing.ListingContractDate),
  );

  const address: Address = {
    CityName: listing.City,
    StateCode: listing.StateOrProvince,
    PostalCode: listing.PostalCode,
    AddressLineText: listing.UnparsedAddress,
  };

  const subjectLatLong = subjectCoordinates
    ? { longitude: subjectCoordinates.lng, latitude: subjectCoordinates.lat }
    : undefined;

  const listingLatLong =
    isDefined(listing.Latitude) && isDefined(listing.Longitude)
      ? { longitude: listing.Longitude, latitude: listing.Latitude }
      : undefined;

  const distanceToSubject =
    isDefined(subjectLatLong) && isDefined(listingLatLong)
      ? +(proximityMiles(subjectLatLong, listingLatLong)?.toFixed(2) ?? 0)
      : undefined;

  const isSelectedComp = comps?.some((c) => c.listingID === listing.ToolkitListingID);

  return {
    t: isSelectedComp ? 'tk' : 'abode',
    id,
    address,
    gla: AloftArea.fromNumber(listing.LivingArea, 'sqft'),
    lotSize: getLotAreaInSqft(listing),
    saleDate,
    contractDate,
    salePrice: AloftPrice.fromDollarLike(listing.ClosePrice),
    listPrice: AloftPrice.fromDollarLike(listing.ListPrice),
    listingContractDate,
    distanceToSubject,
    imageUrls: listing.Media?.map((m) => m.MediaURL),
    latitude: listing.Latitude,
    longitude: listing.Longitude,
    fullBaths: listing.BathroomsFull,
    halfBaths: listing.BathroomsHalf,
    bedroomCount: listing.BedroomsTotal,
    yearBuilt: listing.YearBuilt,
    bedrooms: listing.BedroomsTotal,
    basement: listing.BasementYN,
    garage: listing.GarageYN,
    quality: undefined,
    condition: undefined,
    cumulativeDaysOnMarket: listing.CumulativeDaysOnMarket || listing.DaysOnMarket,
    listingID: listing.ToolkitListingID || (listing.ListingId as ToolkitListingID),
    mlsStatus: listing.MlsStatus,
    reo: listing.SpecialListingConditions?.includes('REO'),
    shortSale: listing.SpecialListingConditions?.includes('Short Sale'),
    garageCount: listing.GarageSpaces,
    pool: listing.PoolPrivateYN,
    propertyType: listing.PropertySubType,
  };
};

export const listingsToCompSelectionProps = (
  subject: CompSelectionProperty,
  comps: CompSelectionProperty[],
  listings: MLSListing[],
  hasArvOption: boolean,
): CompSelectionProperty[] => {
  const properties = listings
    .map((listing) => buildCompSelectionProperty(listing, subject, comps))
    .filter(isDefined);

  addSimilarityScores(properties, subject);

  if (hasArvOption) {
    addQualityTiers(properties);
  }
  return properties;
};
